/*
 * 50-50 Large Promo video controls — scoped per [data-large-promo-root] root.
 * Loaded only when at least one promo block has a video.
 * Hooks: [data-large-promo-root], [data-media-card], [data-video-toggle], [data-audio-toggle].
 */

class LargePromoVideos {
  constructor(root) {
    this.root = root;
    this.buttons = root.querySelectorAll('[data-video-toggle]');
    this.audioButtons = root.querySelectorAll('[data-audio-toggle]');
    this.videos = root.querySelectorAll('video');

    if (!this.videos.length) return;

    this.init();
  }

  init() {
    this.initVideoControls();
    this.initAudioControls();
    this.initViewportPause();
  }

  initVideoControls() {
    this.buttons.forEach((btn) => {
      const video = btn.closest('[data-media-card]')?.querySelector('video');
      if (!video) return;

      btn.setAttribute('data-video-ready', 'true');
      video.controls = false;

      video.addEventListener('play', () => {
        if (video.dataset.inViewport === 'false' && video.dataset.userPaused !== 'true') {
          video.dataset.autoPaused = 'true';
          video.pause();
          return;
        }
        this.setVideoState(btn, video);
      });

      video.addEventListener('pause', () => this.setVideoState(btn, video));

      btn.addEventListener('click', (event) => {
        event.preventDefault();
        if (video.paused) {
          delete video.dataset.userPaused;
          delete video.dataset.autoPaused;
          video.play().catch(() => {});
        } else {
          video.dataset.userPaused = 'true';
          delete video.dataset.autoPaused;
          video.pause();
        }
        this.setVideoState(btn, video);
      });

      // Seed optimistic: assume the video will autoplay, so the discrete-mode
      // button stays hidden. Only flip to paused if autoplay is genuinely blocked.
      btn.dataset.videoState = 'playing';
      btn.setAttribute('aria-label', 'Pause video');
      btn.setAttribute('aria-pressed', 'true');
      this.mirrorAudioButtonState(btn.closest('[data-media-card]'), 'playing');

      const playProbe = video.play();
      if (playProbe && typeof playProbe.then === 'function') {
        // An AbortError is our own pause() interrupting the probe; only a refusal means paused.
        playProbe.catch((err) => {
          if (err && err.name === 'AbortError') return;
          this.setVideoState(btn, video, { force: true });
        });
      }
    });
  }

  // Mirrors `data-video-state` onto the audio button so both controls reveal together.
  mirrorAudioButtonState(mediaCard, videoState) {
    const audioBtn = mediaCard?.querySelector('[data-audio-toggle]');
    if (audioBtn) audioBtn.dataset.videoState = videoState;
  }

  initAudioControls() {
    this.audioButtons.forEach((btn) => {
      const video = btn.closest('[data-media-card]')?.querySelector('video');
      if (!video) return;

      btn.setAttribute('data-video-ready', 'true');
      this.setAudioState(btn, video.muted);

      btn.addEventListener('click', (event) => {
        event.preventDefault();
        const nextMuted = !video.muted;
        if (!nextMuted) this.muteOthers(video);
        video.muted = nextMuted;
        this.setAudioState(btn, nextMuted);
      });
    });
  }

  // Both promos autoplay side by side, so audio is exclusive: unmuting one mutes the other.
  muteOthers(activeVideo) {
    this.audioButtons.forEach((btn) => {
      const video = btn.closest('[data-media-card]')?.querySelector('video');
      if (!video || video === activeVideo) return;
      video.muted = true;
      this.setAudioState(btn, true);
    });
  }

  setAudioState(btn, isMuted) {
    if (!btn) return;
    btn.setAttribute('aria-label', isMuted ? 'Unmute audio' : 'Mute audio');
    btn.setAttribute('aria-pressed', String(!isMuted));
    btn.dataset.audioState = isMuted ? 'muted' : 'unmuted';
  }

  initViewportPause() {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      this.videos.forEach((video) => {
        video.dataset.userPaused = 'true';
        video.pause();
      });
      this.updateButtons();
      return;
    }

    if (!('IntersectionObserver' in window)) return;

    // Observe the section root so horizontal carousel scroll doesn't pause inactive slides.
    // Videos only pause when the whole section leaves the viewport vertically.
    this.viewportObserver = new IntersectionObserver(
      ([entry]) => {
        const inViewport = entry.isIntersecting;

        this.videos.forEach((video) => {
          video.dataset.inViewport = inViewport ? 'true' : 'false';

          if (inViewport) {
            if (video.dataset.autoPaused === 'true' && video.dataset.userPaused !== 'true') {
              delete video.dataset.autoPaused;
              video.play().catch(() => {
                video.dataset.autoPaused = 'true';
              });
            }
            return;
          }

          if (!video.paused && video.dataset.userPaused !== 'true') {
            video.dataset.autoPaused = 'true';
            video.pause();
          }
        });

        this.updateButtons();
      },
      { threshold: 0.1 },
    );

    this.viewportObserver.observe(this.root);
  }

  updateButtons() {
    this.buttons.forEach((btn) => {
      const video = btn.closest('[data-media-card]')?.querySelector('video');
      if (video) this.setVideoState(btn, video);
    });
  }

  setVideoState(btn, video, options) {
    if (!btn || !video) return;
    // A pause before playback has begun is start-up noise from our own viewport/breakpoint handling,
    // not a user action. `force` marks the cases that must still show the button.
    if (!(options && options.force) && video.paused && video.played.length === 0) return;
    const isPaused = video.paused;
    btn.setAttribute('aria-label', isPaused ? 'Play video' : 'Pause video');
    btn.setAttribute('aria-pressed', String(!isPaused));
    btn.dataset.videoState = isPaused ? 'paused' : 'playing';
    this.mirrorAudioButtonState(btn.closest('[data-media-card]'), btn.dataset.videoState);
  }
}

function initSections(container = document) {
  const selector = '[data-large-promo-root]:not([data-promos-videos-init])';
  const roots = container.matches?.(selector) ? [container] : container.querySelectorAll(selector);
  roots.forEach((root) => {
    root.dataset.promosVideosInit = 'true';
    new LargePromoVideos(root);
  });
}

initSections();
document.addEventListener('shopify:section:load', (event) => initSections(event.target));
